import { compact, get } from 'lodash';
import { createSelector } from '@reduxjs/toolkit';
import { resourceType } from 'constants/loading';
import { exportPrimaryStatusTypes, exportScopeTypes } from 'constants/exportSelector';
import {
  getActiveFieldUuid,
  getActiveOperationUuid,
  getActiveProductYear,
  getActiveScriptType
} from 'current/selectors';
import { isResourceTypePending } from 'loading/selectors';
import { getFieldsForWorkPlanCreation, getJohnDeereFields } from 'affiliates/selectors';
import { Store } from 'store/types/types';

export const getExportSelectorAllOptions = (state: Store) => get(state, 'exportSelector.options');
export const getExportSelectorOptions = createSelector([
  (state: Store) => getActiveScriptType(state),
  (state: Store) => getActiveProductYear(state),
  (state: Store) => getActiveOperationUuid(state),
  (state: Store) => getActiveFieldUuid(state),
  (state: Store) => getExportSelectorAllOptions(state)
],
(scriptType, productYear, operationUuid, fieldUuid, options) => {
  const productYearTree = options?.[scriptType]?.[productYear];
  if (productYearTree) {
    const { operations, fields } = productYearTree;
    const fieldOptions = fields[fieldUuid] || [];
    const operationOptions = operationUuid
      ? operations[operationUuid]
      : operations.all;

    return compact([].concat(operationOptions, fieldOptions));
  }

  return [];
});
export const getExportParams = (state: Store) => get(state, 'exportSelector.params');
export const getRawScriptList = (state: Store) => get(state, 'exportSelector.rawScriptList');
export const onlyExportPrimaryScripts = (_: Store, param: string) => param === exportPrimaryStatusTypes.PRIMARY;
export const isFvFieldSelected = (state: Store, fieldUuid: string) =>
  state.exportSelector?.isFvFieldSelected?.[fieldUuid];
export const areExportOptionsPending = createSelector([
  (state: Store) => isResourceTypePending({ state, type: resourceType.DEVICE_LIST }),
  (state: Store) => isResourceTypePending({ state, type: resourceType.AFFILIATE_LIST }),
  (state: Store) => isResourceTypePending({ state, type: resourceType.RAW_SCRIPTS_LIST })
],
(...status) => status.some(Boolean)
);

/**
 * Checks if any fields that can be used to create Work Plans were selected.
 * This could be done explicitly by the user in the operation flow or implicitly
 * by setting a JD field match for the FV field in the field or single script flows.
 */
export const isAnyFieldSelectedForWorkPlan = createSelector([
  (state: Store) => getExportParams(state),
  (state: Store) => getFieldsForWorkPlanCreation(state),
  (state: Store) => state.exportSelector.isFvFieldSelected,
  (state: Store) => getJohnDeereFields(state),
  (state: Store) => getActiveFieldUuid(state)
],
(exportParams, fieldsForWorkPlanCreation, selectedFvFields, johnDeereFields, activeFieldUuid) => {
  if (exportParams?.scopeType === exportScopeTypes.OPERATION) {
    return fieldsForWorkPlanCreation.some(({ uuid }) => selectedFvFields[uuid]);
  }

  return johnDeereFields.find(({ fvFieldUuid }) => fvFieldUuid === activeFieldUuid);
});
